<template>
  <div class="page-shell">
      <AppTab v-model="activeTab" :items="tabs" :columns="4" />
      <div>
        <AppTextField
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          hide-details
          placeholder="Search..."
        />
      </div>
      <p class="result-info">
        {{ pagination.total > 0 ? `${quotes.length} of ${pagination.total} results` : `${quotes.length} results` }}
      </p>
      <p v-if="errorMessage" class="result-error">{{ errorMessage }}</p>
      <p v-if="isLoading" class="result-info">Loading quotations...</p>

      <div class="quote-list">
        <AppCard
          v-for="item in quotes"
          :key="item.id"
          class="quote-card"
          @click="handleOpen(item.id)"
        >
          <div class="quote-left">
            <div
              class="quote-icon"
              :class="[item.iconTone, { 'quote-icon--with-logo': hasValidLogo(item.vendorLogo) }]"
            >
              <img
                v-if="hasValidLogo(item.vendorLogo)"
                :src="item.vendorLogo"
                :alt="item.vendor"
                class="quote-logo"
    
              />
              <v-icon v-else size="18">mdi-store-outline</v-icon>
            </div>
            <div class="quote-copy">
              <div class="quote-top">
                <p class="quote-title">{{ item.vendor }}</p>
                <v-chip :color="getStatusColor(item.status)" size="small" label>
                  {{ item.status }}
                </v-chip>
              </div>
              <p class="quote-subtitle">{{ item.service }}</p>
              <div class="quote-meta">
                <span class="quote-price">{{ item.amount }}</span>
                <span class="dot">•</span>
                <span>{{ item.date }}</span>
                <!-- <span class="dot">•</span> -->
                <!-- <span>
                  <v-chip color="primary" size="small" label>{{ item.quotes_count }} quotes</v-chip>
                </span> -->
              </div>
            </div>
          </div>
          <v-icon size="18" class="quote-chevron">mdi-chevron-right</v-icon>
        </AppCard>
      </div>
      <v-btn
        v-if="hasMore"
        variant="outlined"
        color="primary"
        :loading="isLoadingMore"
        @click="loadMore"
      >
        Load more
      </v-btn>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import AppTab from "@/shared/components/ui/AppTab.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import { fetchVendorQuotations } from "@/shared/services/quotations.api";
import AppCard from "@/shared/components/ui/AppCard.vue";
import { getStatusColor } from "@/shared/utils/utils";

const tabs = [
  { label: "Received", value: "new" },
  { label: "Accepted", value: "accepted" },
  { label: "Declined", value: "rejected" },
  { label: "All", value: "all" },
];
const activeTab = ref("new");
const searchQuery = ref("");
const isLoading = ref(false);
const errorMessage = ref("");
const router = useRouter();

type QuoteItem = {
  id: number;
  vendor: string;
  vendorLogo: string;
  service: string;
  amount: string;
  date: string;
  status: string;
  iconTone: string;
  quotes_count: number;
};

type QuoteApiRecord = Record<string, unknown>;
type QuoteQueryParams = Record<string, string>;
type QuoteListMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

const quotes = ref<QuoteItem[]>([]);
const pagination = ref<QuoteListMeta>({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
});
const hasMore = ref(false);
const isLoadingMore = ref(false);
let loadRequestId = 0;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const statusStyleMap: Record<string, { iconTone: string }> = {
  received: { iconTone: "quote-icon--peach" },
  accepted: { iconTone: "quote-icon--mint" },
  declined: { iconTone: "quote-icon--rose" },
};

function asString(value: unknown, fallback = "") {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
}

function normalizeQuote(item: QuoteApiRecord): QuoteItem {
  const rawStatus = asString(item.status, "Received");
  const status = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
  const style = statusStyleMap[rawStatus.toLowerCase()] || statusStyleMap.received;

  return {
    id: Number(item.id) || 0,
    vendor: asString(item.vendor_name || item.vendor || item.provider_name, "Vendor"),
    vendorLogo: asString(item.vendor_logo || item.logo || item.vendor_image, ""),
    service: asString(item.service_name || item.service || item.title, "Service"),
    amount: asString(item.amount || item.total_amount || item.price, "-"),
    date: asString(item.date || item.created_at || item.quotation_date, "-"),
    status,
    iconTone: style.iconTone,
    quotes_count: Number(item.quotes_count) || 0,
  };
}

function extractQuoteList(payload: unknown): QuoteApiRecord[] {
  if (Array.isArray(payload)) {
    return payload as QuoteApiRecord[];
  }

  const wrapped = payload as { data?: unknown; quotations?: unknown; quotes?: unknown };
  if (Array.isArray(wrapped?.data)) return wrapped.data as QuoteApiRecord[];
  if (Array.isArray(wrapped?.quotations)) return wrapped.quotations as QuoteApiRecord[];
  if (Array.isArray(wrapped?.quotes)) return wrapped.quotes as QuoteApiRecord[];
  return [];
}

function extractMeta(payload: unknown): QuoteListMeta | null {
  const wrapped = payload as { meta?: Record<string, unknown> };
  if (!wrapped?.meta) return null;
  const meta = wrapped.meta;
  const currentPage = Number(meta.current_page || 1);
  const lastPage = Number(meta.last_page || 1);
  const perPage = Number(meta.per_page || 10);
  const total = Number(meta.total || 0);

  return {
    current_page: Number.isNaN(currentPage) ? 1 : currentPage,
    last_page: Number.isNaN(lastPage) ? 1 : lastPage,
    per_page: Number.isNaN(perPage) ? 10 : perPage,
    total: Number.isNaN(total) ? 0 : total,
  };
}

async function loadQuotations(append = false) {
  const requestId = ++loadRequestId;
  const nextPage = append ? pagination.value.current_page + 1 : 1;

  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
  }
  errorMessage.value = "";

  try {
    const params: QuoteQueryParams = {
      per_page: "10",
      page: String(nextPage),
    };
    const query = searchQuery.value.trim();
    if (query) params.search = query;
    if (activeTab.value !== "all") params.status = activeTab.value;

    const response = await fetchVendorQuotations(params);
    if (requestId !== loadRequestId) return;

    const normalized = extractQuoteList(response).map(normalizeQuote);
    quotes.value = append ? [...quotes.value, ...normalized] : normalized;

    const meta = extractMeta(response);
    if (meta) {
      pagination.value = meta;
      hasMore.value = meta.current_page < meta.last_page;
    } else {
      pagination.value = {
        current_page: nextPage,
        last_page: nextPage,
        per_page: 10,
        total: quotes.value.length,
      };
      hasMore.value = false;
    }
  } catch (error) {
    if (requestId !== loadRequestId) return;
    console.error("Failed to load quotations", error);
    errorMessage.value = "Unable to load quotations right now.";
    if (!append) {
      quotes.value = [];
      pagination.value = { current_page: 1, last_page: 1, per_page: 10, total: 0 };
      hasMore.value = false;
    }
  } finally {
    if (requestId !== loadRequestId) return;
    if (append) {
      isLoadingMore.value = false;
    } else {
      isLoading.value = false;
    }
  }
}

// function statusClass(status: string) {
//   return `quote-status--${status.toLowerCase()}`;
// }

function hasValidLogo(url: string) {
  if (!url) return false;
  const normalized = url.toLowerCase();
  return !normalized.includes("noimage.web");
}

function handleOpen(id: number) {
  router.push({ name: "vendor.quotations.detail", params: { id } });
}

function loadMore() {
  if (isLoadingMore.value || !hasMore.value) return;
  loadQuotations(true);
}

onMounted(loadQuotations);

watch(activeTab, () => {
  loadQuotations();
});

watch(searchQuery, () => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    loadQuotations();
  }, 350);
});

onBeforeUnmount(() => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
});
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
}

.quote-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quote-card {
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: left;
}

.quote-card:focus-visible {
  outline: 2px solid #f97316;
  outline-offset: 2px;
}

.quote-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.quote-icon {
  width: 42px;
  height: 42px;
  border-radius: var(--radius-lg);
  display: grid;
  place-items: center;
  overflow: hidden;
}

.quote-icon--with-logo {
  padding: 3px;
}

.quote-logo {
  width: 100%;
  height: 100%;
  border-radius: var(--radius-lg);
  object-fit: contain;
}

.quote-icon--peach {
  background: #fff1f2;
  color: #f97316;
}

.quote-icon--sky {
  background: #e0f2fe;
  color: #0ea5e9;
}

.quote-icon--mint {
  background: #dcfce7;
  color: #16a34a;
}

.quote-icon--amber {
  background: #fef3c7;
  color: #f59e0b;
}

.quote-icon--rose {
  background: #ffe4e6;
  color: #f43f5e;
}

.quote-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.quote-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.quote-title {
  margin: 0;
  font-weight: 700;
  font-size: 14px;
  color: var(--color-title);
}

.quote-subtitle {
  margin: 0;
  font-size: var(--font-size-default);
  font-weight: 500;
  color: var(--color-subtitle-light);
}

.quote-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-default);
  color: var(--color-subtitle-light);
}

.quote-price {
  color: #f97316;
  font-weight: 700;
}

.dot {
  color: #cbd5f5;
}

.quote-status {
  font-size: var(--font-size-default);
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.quote-status--received {
  color: #d97706;
  background: #fef3c7;
}

.quote-status--accepted {
  color: #16a34a;
  background: #dcfce7;
}

.quote-status--declined {
  color: #f43f5e;
  background: #ffe4e6;
}

.quote-chevron {
  color: #94a3b8;
}
</style>
